/**
 * A-16 — every order, to track where it stands: search (order reference, patient name or phone),
 * status, payment and placed-date filters, sort (+ direction) and a batch size; a server-paged
 * table that loads as you scroll (components/ui/VirtualTable). A row opens the order: its items
 * and GST, payments, refunds and status history (AdminOrderDetail). The view lives in the URL
 * (?q=&status=&payment=&from=&to=&sort=&dir=&per=&order=), so it can be reloaded or shared.
 */

import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";

import { listAdminOrders } from "../../api/adminOrders";
import type { AdminOrder, AdminOrderQuery, OrderPaymentFilter, OrderSort } from "../../api/adminOrders";
import { listCatalogueStatuses } from "../../api/catalogue";
import { describeError } from "../../api/errors";
import { ExportButton } from "../../components/ui/ExportButton";
import {
  BatchSizeFilter,
  ClearFilters,
  FilterBar,
  FilterField,
  SearchBox,
  SelectFilter,
  SortFilter,
} from "../../components/ui/FilterBar";
import { Modal } from "../../components/ui/Modal";
import { TableRow, VirtualTable } from "../../components/ui/VirtualTable";
import { formatINR } from "../../lib/money";
import { phoneLabel } from "../../lib/phone";
import type { TableColumn } from "../../lib/tableColumns";
import { byPageNumber, EXPORT_PAGE_SIZE, sheetDateTime, sheetPhone, type ExportColumn } from "../../lib/tableExport";
import { useSearchBox, useUrlParams } from "../../lib/urlState";
import { AdminOrderDetail } from "./AdminOrderDetail";

const PAYMENT_OPTIONS: { value: OrderPaymentFilter; label: string }[] = [
  { value: "", label: "All" },
  { value: "paid", label: "Paid" },
  { value: "unpaid", label: "Not paid" },
  { value: "refunded", label: "Refunded" },
];
const SORTS: { value: OrderSort; label: string }[] = [
  { value: "placed", label: "Placed" },
  { value: "amount", label: "Amount" },
  { value: "paid", label: "Paid" },
  { value: "patient", label: "Patient" },
];
// The direction each sort reads most naturally in, applied when the sort changes.
const NATURAL_DESC: Record<OrderSort, boolean> = { placed: true, amount: true, paid: true, patient: false };

// The order and its amount at every width; status from sm, patient and payment from md, items from
// lg, pharmacist from xl (narrower screens show what matters under the order).
const COLUMNS: TableColumn[] = [
  { key: "order", header: "Order", width: { base: "minmax(0,1fr)", sm: "8.5rem" } },
  { key: "patient", header: "Patient", width: "minmax(0,1.4fr)", from: "sm" },
  { key: "status", header: "Status", width: "8.5rem", from: "sm" },
  { key: "items", header: "Items", width: "3.5rem", from: "lg", align: "end" },
  { key: "amount", header: "Amount", width: "6.25rem", align: "end" },
  { key: "payment", header: "Payment", width: "minmax(0,1fr)", from: "md" },
  { key: "pharmacist", header: "Pharmacist", width: "minmax(0,1fr)", from: "xl" },
];

const EXPORT_COLUMNS: ExportColumn<AdminOrder>[] = [
  { header: "Order", value: (o) => o.order_id },
  { header: "Placed (IST)", value: (o) => sheetDateTime(o.created_at) },
  { header: "Status", value: (o) => o.status_label },
  { header: "Patient", value: (o) => o.patient_name },
  { header: "Patient phone", value: (o) => sheetPhone(o.patient_phone) },
  { header: "City", value: (o) => o.city },
  { header: "State", value: (o) => o.state_code },
  { header: "Items", value: (o) => o.line_count },
  { header: "Amount (₹)", value: (o) => (o.pricing_published_at ? o.final_amount : "") },
  { header: "Paid (IST)", value: (o) => sheetDateTime(o.paid_at) },
  { header: "Cancelled (IST)", value: (o) => sheetDateTime(o.cancelled_at) },
  { header: "Refunded (₹)", value: (o) => o.refunded_amount },
  { header: "Pharmacist", value: (o) => o.assignee_name },
];

const shortWhen = (iso: string) => new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });

export function Orders() {
  const [params, setParams] = useUrlParams();
  const q = params.get("q") ?? "";
  const status = params.get("status") ?? "";
  const payment = (params.get("payment") ?? "") as OrderPaymentFilter;
  const from = params.get("from") ?? "";
  const to = params.get("to") ?? "";
  const sort = (SORTS.some((s) => s.value === params.get("sort")) ? params.get("sort") : "placed") as OrderSort;
  const descending = params.get("dir") ? params.get("dir") !== "asc" : NATURAL_DESC[sort];
  const perPage = [25, 50, 100].includes(Number(params.get("per"))) ? Number(params.get("per")) : 25;
  const opened = params.get("order");
  const search = useSearchBox(q, (text) => setParams({ q: text }));
  const rangeInvalid = !!from && !!to && from > to;
  // Opening an order is a step in history: Back closes it.
  const navigate = useNavigate();
  const orderHref = (id: string) => {
    const next = new URLSearchParams(params);
    next.set("order", id);
    return `?${next.toString()}`;
  };

  const statuses = useQuery({ queryKey: ["catalogue-statuses"], queryFn: listCatalogueStatuses, staleTime: 5 * 60_000 });
  const statusOptions = [
    { value: "", label: "All" },
    ...(statuses.data ?? [])
      .slice()
      .sort((a, b) => a.display_order - b.display_order)
      .map((s) => ({ value: s.code, label: s.label })),
  ];

  const query: AdminOrderQuery = useMemo(
    () => ({ q, status, payment, placed_from: from, placed_to: to, sort, descending, per_page: perPage }),
    [q, status, payment, from, to, sort, descending, perPage],
  );
  const list = useInfiniteQuery({
    queryKey: ["admin-orders", query],
    queryFn: ({ pageParam }) => listAdminOrders({ ...query, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.has_more ? last.page + 1 : undefined),
    enabled: !rangeInvalid,
    // The current rows stay while a new search or filter loads, instead of a blank table.
    placeholderData: keepPreviousData,
  });
  const rows = useMemo(() => list.data?.pages.flatMap((p) => p.data) ?? [], [list.data]);

  const narrowing = (status ? 1 : 0) + (payment ? 1 : 0) + (from || to ? 1 : 0);
  const activeFilters = narrowing + (q ? 1 : 0);

  return (
    <div className="flex h-full flex-col gap-3 sm:gap-5">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Orders</h2>
          <p className="text-sm text-gray-500">Every order placed: where it stands, its payment and refunds.</p>
        </div>
        <ExportButton
          table="orders"
          columns={EXPORT_COLUMNS}
          keyOf={(o) => o.id}
          disabled={rangeInvalid}
          fetchPage={byPageNumber((page) => listAdminOrders({ ...query, page, per_page: EXPORT_PAGE_SIZE }))}
        />
      </div>

      <FilterBar
        label="Search and filter orders"
        narrowing={narrowing}
        search={<SearchBox value={search.text} onChange={search.onChange} placeholder="Order no., patient name or phone…" />}
      >
        <SelectFilter label="Status" value={status} options={statusOptions} onChange={(v) => setParams({ status: v })} testId="orders-filter-status" />
        <SelectFilter label="Payment" value={payment} options={PAYMENT_OPTIONS} onChange={(v) => setParams({ payment: v })} testId="orders-filter-payment" />
        <FilterField label="Placed from">
          <input
            type="date"
            value={from}
            max={to || undefined}
            onChange={(e) => setParams({ from: e.target.value })}
            aria-invalid={rangeInvalid}
            className={`rounded-lg border px-2 py-1 ${rangeInvalid ? "border-red-500" : ""}`}
          />
        </FilterField>
        <FilterField label="Placed to">
          <input
            type="date"
            value={to}
            min={from || undefined}
            onChange={(e) => setParams({ to: e.target.value })}
            aria-invalid={rangeInvalid}
            className={`rounded-lg border px-2 py-1 ${rangeInvalid ? "border-red-500" : ""}`}
          />
        </FilterField>
        <SortFilter
          value={sort}
          options={SORTS}
          descending={descending}
          onChange={(s) => setParams({ sort: s === "placed" ? null : s, dir: null })}
          onToggle={() => setParams({ dir: descending ? "asc" : "desc" })}
        />
        <BatchSizeFilter value={perPage} onChange={(n) => setParams({ per: n === 25 ? null : String(n) })} />
        <ClearFilters
          count={activeFilters}
          onClear={() => {
            search.cancel();
            setParams({ q: null, status: null, payment: null, from: null, to: null });
          }}
        />
      </FilterBar>
      {rangeInvalid && (
        <p role="alert" className="-mt-2 shrink-0 text-xs text-red-600">
          The “Placed from” date is after the “Placed to” date.
        </p>
      )}

      <VirtualTable
        label="Orders"
        columns={COLUMNS}
        rows={rows}
        rowKey={(o) => o.id}
        rowHeight={60}
        measureRows
        loading={list.isLoading}
        error={list.isError ? describeError(list.error) : null}
        onRetry={() => void list.refetch()}
        empty={activeFilters ? "No orders match your filters." : "No orders yet."}
        total={list.data?.pages[0]?.total}
        hasMore={list.hasNextPage && !list.isPlaceholderData}
        loadingMore={list.isFetchingNextPage}
        loadMoreFailed={list.isFetchNextPageError}
        onLoadMore={list.fetchNextPage}
        resetKey={JSON.stringify(query)}
        renderRow={(o) => (
          // The order number is the link (keyboard, screen readers, a new tab); the rest of the row
          // is a shortcut to it for the mouse.
          <div
            onClick={(e) => {
              if (!(e.target as HTMLElement).closest("a")) navigate(orderHref(o.id));
            }}
            className={`cursor-pointer transition-colors hover:bg-gray-50 ${list.isPlaceholderData ? "opacity-60" : ""}`}
          >
            <OrderRow order={o} href={orderHref(o.id)} />
          </div>
        )}
      />

      <Modal open={!!opened} title="Order" size="lg" onClose={() => setParams({ order: null })}>
        {opened && <AdminOrderDetail orderId={opened} />}
      </Modal>
    </div>
  );
}

function OrderRow({ order: o, href }: { order: AdminOrder; href: string }) {
  const priced = !!o.pricing_published_at;
  const refunded = Number(o.refunded_amount) > 0;
  const paymentText = o.paid_at ? `Paid ${shortWhen(o.paid_at)}` : o.cancelled_at ? "Cancelled" : "Not paid";
  return (
    <TableRow
      columns={COLUMNS}
      className="min-h-15 py-2 text-sm"
      cells={{
        order: (
          <div className="min-w-0">
            <Link to={href} className="font-mono text-xs font-semibold text-gray-800 hover:text-brand hover:underline">
              {o.order_id}
            </Link>
            <div className="text-xs text-gray-400">{shortWhen(o.created_at)}</div>
            {/* What the patient and status columns show from sm up. */}
            <div className="mt-1 flex min-w-0 flex-wrap items-center gap-1.5 sm:hidden">
              <span className="min-w-0 truncate text-xs text-gray-600">{o.patient_name ?? "—"}</span>
              <StatusBadge order={o} />
            </div>
          </div>
        ),
        patient: (
          <div className="min-w-0">
            <div className="truncate text-gray-800">{o.patient_name ?? "—"}</div>
            {o.patient_phone && <div className="truncate text-xs text-gray-400">{phoneLabel(o.patient_phone)}</div>}
          </div>
        ),
        status: <StatusBadge order={o} />,
        items: <span className="tabular-nums text-gray-600">{o.line_count}</span>,
        amount: (
          <div>
            <div className="tabular-nums text-gray-800">{priced ? formatINR(o.final_amount) : "—"}</div>
            <div className="text-xs text-gray-400 md:hidden">{o.paid_at ? "Paid" : o.cancelled_at ? "Cancelled" : priced ? "Not paid" : "Pricing"}</div>
          </div>
        ),
        payment: (
          <div className="min-w-0">
            <div className={`truncate ${o.paid_at ? "text-gray-700" : "text-gray-400"}`}>{paymentText}</div>
            {refunded && <div className="truncate text-xs text-amber-700">Refunded {formatINR(o.refunded_amount)}</div>}
          </div>
        ),
        pharmacist: <span className="block truncate text-gray-600">{o.assignee_name ?? "—"}</span>,
      }}
    />
  );
}

function StatusBadge({ order: o }: { order: AdminOrder }) {
  const tone = o.cancelled_at
    ? "bg-red-50 text-red-700"
    : o.status_final
      ? "bg-emerald-50 text-emerald-700"
      : o.paid_at
        ? "bg-sky-50 text-sky-700"
        : "bg-amber-50 text-amber-700";
  return <span className={`inline-flex w-fit max-w-full truncate rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}>{o.status_label}</span>;
}
